import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { ApiError } from '../../api/client'
import { adAccountsQuery, adAccountsRead, adAccountsTableQuery, type AdAccountsOverview, type AdCheckKey, type AdDomain } from '../../api/securiteeAdAccounts'
import { escapeHtml } from '../../inventoree/format'
import { Content } from '../../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Spinner } from '../../ui/Spinner'
import './ad-accounts.css'

/**
 * Securitee > AD accounts: conturile din Active Directory citite de agentul de
 * pe controlorul de domeniu, o data pe zi sau la "Read now". Numai afisare:
 * fara parole, fara hash-uri, nimic schimbat in AD. Cardurile verificarilor
 * filtreaza tabelul.
 */
export const TABLE_ID = 'securiteeAdAccounts'

/* Cat se reciteste ecranul cat asteapta o citire ceruta. */
const PENDING_REFETCH_MS = 10_000

export function AdAccountsPage() {
  const client = useQueryClient()
  const page = useQuery({ ...adAccountsQuery(), refetchInterval: (q) => (q.state.data?.read.pending ? PENDING_REFETCH_MS : false) })
  const [check, setCheck] = useState<AdCheckKey | ''>('')
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 0, dir: 'asc' }, search: '' })
  const [readNote, setReadNote] = useState('')
  const [reading, setReading] = useState(false)
  const data = page.data
  const columns = useMemo(() => adColumns(data?.checks ?? []), [data?.checks])
  const orderColumn = columns.find((c) => c.index === state.order.column && c.orderable) ?? columns[0]!
  const stamp = (data?.domains ?? []).map((d) => d.collected_at).join('|')
  const table = useQuery({
    ...adAccountsTableQuery({ draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search }, check, stamp),
    enabled: (data?.domains.length ?? 0) > 0,
    placeholderData: keepPreviousData,
  })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <Content>
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
        </div>
      </Content>
    )
  }
  if (!data) return <Spinner />

  const pick = (next: AdCheckKey | '') => {
    setCheck(next)
    setState({ start: 0 })
  }
  const readNow = async () => {
    setReading(true)
    try {
      const r = await adAccountsRead()
      setReadNote(
        r.queued > 0
          ? `Asked ${r.queued} domain controller${r.queued === 1 ? '' : 's'} to read the accounts now. They show here in about a minute.`
          : 'A read is already waiting on the domain controller.',
      )
      await client.invalidateQueries({ queryKey: adAccountsQuery().queryKey, exact: true })
    } catch (e) {
      setReadNote(e instanceof Error ? e.message : 'The read could not be asked.')
    } finally {
      setReading(false)
    }
  }

  return (
    <Content>
      <div id="content" className="ad-page">
        <header className="panel-heading">
          <h3>
            <strong>AD accounts</strong>
          </h3>
          <label className="color">Active Directory accounts read on the domain controllers, once a day · read only</label>
        </header>

        {data.domains.map((d) => (
          <DomainLines key={d.domain} domain={d} />
        ))}
        <ReadNow data={data} busy={reading} note={readNote} onRead={() => void readNow()} />

        {data.domains.length === 0 ? (
          <p className="ad-empty">
            {data.read.dcs > 0
              ? 'No accounts yet. The OptimumDesk agent on the domain controller sends them once a day; Read now asks it now.'
              : 'No domain controller of this company runs the OptimumDesk agent yet.'}
          </p>
        ) : (
          <>
            <Cards data={data} check={check} onPick={pick} />
            <DataTable
              id={TABLE_ID}
              columns={columns}
              rows={table.data?.data ?? []}
              total={table.data?.recordsTotal ?? 0}
              fetching={table.isFetching}
              error={table.isError ? (table.error as Error).message : null}
              onRetry={() => void table.refetch()}
              state={state}
              onState={setState}
              rowId={(r) => String(r.DT_RowId)}
            />
          </>
        )}
      </div>
    </Content>
  )
}

function DomainLines({ domain: d }: { domain: AdDomain }) {
  const p = d.policy
  return (
    <>
      <p className="ad-domain">
        <strong>{d.domain}</strong> · read on {d.device_name ?? `device #${d.device_id}`} · last read {d.collected_at.slice(0, 16)} UTC · {d.account_count} accounts
      </p>
      {p && (
        <p className="ad-policy">
          Password policy: minimum {p.min_length} characters · {p.max_age_days > 0 ? `expires after ${p.max_age_days} days` : 'never expires'} · complexity{' '}
          {p.complexity ? 'on' : 'off'} · remembers {p.history} · {p.lockout_threshold > 0 ? `lockout after ${p.lockout_threshold} wrong passwords` : 'no lockout'}
          {d.policy_findings.map((f) => (
            <span key={f} className="label label-warning ad-policy-finding">
              {f}
            </span>
          ))}
        </p>
      )}
      {d.hardening && d.hardening.checks.length > 0 && (
        <div className="ad-hardening">
          <p className="ad-hardening-head">Domain hardening · read on {d.hardening.dc || d.device_name || `device #${d.device_id}`}</p>
          <ul>
            {d.hardening.checks.map((c) => (
              <li key={c.key} className={c.ok ? 'ad-hardening-ok' : 'ad-hardening-bad'}>
                <strong>{c.title}</strong>
                {c.detail && <span className="ad-hardening-detail"> · {c.detail}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

function ReadNow({ data, busy, note, onRead }: { data: AdAccountsOverview; busy: boolean; note: string; onRead(): void }) {
  if (data.read.dcs === 0) return null
  const waiting = busy || data.read.pending
  return (
    <div className="ad-read">
      <button type="button" className="btn btn-default btn-sm" disabled={waiting} onClick={onRead}>
        <i className={waiting ? 'fas fa-circle-notch fa-spin' : 'fas fa-sync-alt'} aria-hidden="true"></i> {waiting ? 'Reading…' : 'Read now'}
      </button>
      {note && <span className="ad-muted">{note}</span>}
    </div>
  )
}

function Cards({ data, check, onPick }: { data: AdAccountsOverview; check: AdCheckKey | ''; onPick(next: AdCheckKey | ''): void }) {
  return (
    <div className="ad-cards" role="group" aria-label="Checks">
      <button type="button" className="ad-card" aria-pressed={check === ''} onClick={() => onPick('')}>
        <span className="ad-card-title">All accounts</span>
        <span className="ad-card-value">{data.total}</span>
      </button>
      {data.checks.map((c) => (
        <button key={c.key} type="button" className={'ad-card' + (c.count > 0 ? ' ad-card-bad' : '')} aria-pressed={check === c.key} onClick={() => onPick(c.key)}>
          <span className="ad-card-title">{c.label}</span>
          <span className="ad-card-value">{c.count}</span>
        </button>
      ))}
    </div>
  )
}

/** Data UTC `YYYY-MM-DD`, cu ora completa in balon; null = niciodata. */
function momentHtml(value: unknown, never = 'never'): string {
  if (typeof value !== 'string' || value === '') return `<span class="ad-muted">${never}</span>`
  return `<span title="${escapeHtml(value)} UTC">${escapeHtml(value.slice(0, 10))}</span>`
}

/** Coloana tabelului, cu numele campului dupa care sorteaza serverul. */
type AdColumn = TableColumn & { name?: string }

function adColumns(checks: AdAccountsOverview['checks']): AdColumn[] {
  const label = Object.fromEntries(checks.map((c) => [c.key, c.label])) as Record<string, string>
  return [
    {
      index: 0,
      title: 'Account',
      name: 'sam',
      orderable: true,
      visible: true,
      // numele afisat, cand spune ceva in plus fata de cont
      html: (r) =>
        `<strong>${escapeHtml(r.sam)}</strong>` +
        (r.name && String(r.name).toLowerCase() !== String(r.sam).toLowerCase() ? `<div class="ad-muted">${escapeHtml(r.name)}</div>` : ''),
    },
    { index: 1, title: 'Domain', name: 'domain', orderable: true, visible: true, html: (r) => escapeHtml(r.domain) },
    { index: 2, title: 'Enabled', name: 'enabled', orderable: true, visible: true, width: 80, html: (r) => (r.enabled ? 'Yes' : '<span class="ad-muted">No</span>') },
    { index: 3, title: 'Password set (UTC)', name: 'pwd_set', orderable: true, visible: true, width: 130, html: (r) => momentHtml(r.pwd_set, 'not set') },
    { index: 4, title: 'Last logon (UTC)', name: 'last_logon', orderable: true, visible: true, width: 130, html: (r) => momentHtml(r.last_logon) },
    { index: 5, title: 'Created (UTC)', name: 'created', orderable: true, visible: true, width: 120, html: (r) => momentHtml(r.created, '-') },
    {
      index: 6,
      title: 'Admin groups',
      name: 'admin_groups',
      orderable: true,
      visible: true,
      html: (r) => (Array.isArray(r.admin_groups) && r.admin_groups.length > 0 ? r.admin_groups.map((g) => escapeHtml(g)).join(', ') : '<span class="ad-muted">-</span>'),
    },
    {
      index: 7,
      title: 'Findings',
      orderable: false,
      visible: true,
      html: (r) =>
        Array.isArray(r.flags) && r.flags.length > 0
          ? r.flags.map((f) => `<span class="label label-warning ad-flag">${escapeHtml(label[String(f)] ?? String(f))}</span>`).join(' ')
          : '<span class="ad-muted">-</span>',
    },
  ]
}
